// Pure grouping of lifts into next-service bands for the 30/60/90 PPM dashboard (Session AJ, Item
// 11). It reads ONLY the LIVE projection that GET /api/assets already computes (serviceOverdue +
// nextScheduledServiceAt + nextServiceTone + hasContract), never the stale stored
// Asset.nextServiceDue, so the dashboard agrees with the Lifts register and the compliance surfaces.
import type { Asset } from "./resources";

export type PpmBandKey = "overdue" | "not_assessed" | "due_0_30" | "due_31_60" | "due_61_90" | "beyond" | "none";

export interface PpmBandMeta {
  key: PpmBandKey;
  label: string;
  tone: "red" | "amber" | "green" | "grey";
}

// The fixed band order. The brief's five bands plus a "none" bucket for a lift with no active
// contract or no scheduled/projected visit, so every lift is accounted for.
export const PPM_BANDS: PpmBandMeta[] = [
  { key: "overdue", label: "Overdue", tone: "red" },
  // Session BDH, D-BDH-7: no recorded service and otherwise overdue (projected from the contract start) reads
  // "not assessed", never red. Mirrors server/src/modules/scheduling/ppmBands.ts.
  { key: "not_assessed", label: "Not assessed: no service recorded", tone: "grey" },
  { key: "due_0_30", label: "Due in 0 to 30 days", tone: "red" },
  { key: "due_31_60", label: "Due in 31 to 60 days", tone: "amber" },
  { key: "due_61_90", label: "Due in 61 to 90 days", tone: "green" },
  { key: "beyond", label: "Beyond 90 days", tone: "grey" },
  { key: "none", label: "No contract or not scheduled", tone: "grey" },
];

const DAY_MS = 86_400_000;

type PpmAsset = Pick<Asset, "serviceOverdue" | "nextScheduledServiceAt" | "nextServiceTone" | "hasContract" | "serviceRecorded">;

// The band a single lift falls into. A missed visit (serviceOverdue) is overdue regardless of the
// next upcoming date; a lift with no contract/date goes to "none". Day boundaries use whole UTC days
// from `now`.
export function ppmBandForAsset(a: PpmAsset, now: Date): PpmBandKey {
  const unrecorded = a.serviceRecorded === false;
  if (a.serviceOverdue) return unrecorded ? "not_assessed" : "overdue";
  if (a.nextServiceTone === "no_contract" || a.hasContract === false) return "none";
  const next = a.nextScheduledServiceAt ? new Date(a.nextScheduledServiceAt) : null;
  if (!next || Number.isNaN(next.getTime())) return "none";
  const days = Math.floor((next.getTime() - now.getTime()) / DAY_MS);
  if (days < 0) return unrecorded ? "not_assessed" : "overdue";
  if (days <= 30) return "due_0_30";
  if (days <= 60) return "due_31_60";
  if (days <= 90) return "due_61_90";
  return "beyond";
}

// Whole days from now until a lift's next scheduled service (negative = overdue, null = no date).
export function daysUntilNextService(a: Pick<Asset, "nextScheduledServiceAt">, now: Date): number | null {
  const next = a.nextScheduledServiceAt ? new Date(a.nextScheduledServiceAt) : null;
  if (!next || Number.isNaN(next.getTime())) return null;
  return Math.floor((next.getTime() - now.getTime()) / DAY_MS);
}

export interface PpmGroup extends PpmBandMeta {
  assets: Asset[];
}

// Group every lift into its band, each band sorted by soonest next due (a lift with no date last).
export function groupAssetsByPpmBand(assets: Asset[], now: Date): PpmGroup[] {
  const byKey = new Map<PpmBandKey, Asset[]>();
  for (const m of PPM_BANDS) byKey.set(m.key, []);
  for (const a of assets) byKey.get(ppmBandForAsset(a, now))!.push(a);
  for (const list of byKey.values()) {
    list.sort((x, y) => {
      const xv = x.nextScheduledServiceAt ? new Date(x.nextScheduledServiceAt).getTime() : Number.POSITIVE_INFINITY;
      const yv = y.nextScheduledServiceAt ? new Date(y.nextScheduledServiceAt).getTime() : Number.POSITIVE_INFINITY;
      return xv - yv;
    });
  }
  return PPM_BANDS.map((m) => ({ ...m, assets: byKey.get(m.key)! }));
}
